import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Settings as SettingsIcon,
  Badge as BadgeIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const ClientUsersPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // State
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Search, Filter, and Pagination State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, pending
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;
  
  // Custom fields configuration state
  const [customFieldsDialogOpen, setCustomFieldsDialogOpen] = useState(false);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [savingCustomFields, setSavingCustomFields] = useState(false);
  const [fieldForm, setFieldForm] = useState({
    fieldName: '',
    displayName: '',
    fieldType: 'text',
    dropdownOptions: [],
    defaultValue: '',
    helpText: '',
    userEditable: true,
    order: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    additionalInfo: '',
    customFields: {},
    viewingPermissions: 'own_executions',
    projectAccess: []
  });

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🚀 [FRONTEND] Starting to fetch data for company:', companyId);
      
      const [companyRes, projectsRes, usersRes, customFieldsRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/companies/${companyId}/projects`),
        api.get(`/admin/companies/${companyId}/client-users`),
        api.get(`/companies/${companyId}/custom-fields`)
      ]);
      
      console.log('📊 [FRONTEND] Client users response:', usersRes.data);
      console.log('📊 [FRONTEND] Number of users received:', usersRes.data?.length || 0);
      
      console.log('📊 [FRONTEND] Company response:', companyRes.data);
      console.log('📊 [FRONTEND] Projects response:', projectsRes.data);
      console.log('📊 [FRONTEND] Projects array length:', projectsRes.data?.length || 0);
      
      setCompany(companyRes.data);
      setProjects(projectsRes.data);
      setClientUsers(usersRes.data);
      setCustomFields(customFieldsRes.data.customClientUserFields || []);
    } catch (error) {
      console.error('❌ [FRONTEND] Error fetching data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const resetForm = () => {
    const initialCustomFields = {};
    customFields.forEach(field => {
      initialCustomFields[field.fieldName] = field.defaultValue || '';
    });
    
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      additionalInfo: '',
      customFields: initialCustomFields,
      viewingPermissions: 'own_executions',
      projectAccess: []
    });
  };

  const handleInviteSubmit = async () => {
    try {
      setCreating(true);
      
      const payload = {
        ...formData,
        projectAccess: formData.projectAccess.map(projectId => ({
          projectId,
          permissions: {
            canExecuteFlow: true,
            canEditData: true,
            canDownloadResults: true,
            canViewHistory: true
          }
        }))
      };

      await api.post(`/admin/companies/${companyId}/client-users`, payload);
      
      showSnackbar('Client user invited successfully! Invitation email sent.');
      setInviteDialogOpen(false);
      resetForm();
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error inviting client user:', error);
      showSnackbar(error.response?.data?.message || 'Error inviting client user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setCreating(true);
      
      const payload = {
        companyId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        additionalInfo: formData.additionalInfo,
        customFields: formData.customFields,
        viewingPermissions: formData.viewingPermissions,
        projectAccess: formData.projectAccess.map(projectId => ({
          projectId,
          permissions: {
            canExecuteFlow: true,
            canEditData: true,
            canDownloadResults: true,
            canViewHistory: true
          }
        })),
        isActive: true
      };

      await api.put(`/admin/client-users/${selectedUser._id}`, payload);
      
      showSnackbar('Client user updated successfully!');
      setEditDialogOpen(false);
      setSelectedUser(null);
      resetForm();
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error updating client user:', error);
      showSnackbar(error.response?.data?.message || 'Error updating client user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleResendInvitation = async (userId) => {
    try {
      await api.post(`/admin/client-users/${userId}/resend-invitation`, {
        companyId
      });
      showSnackbar('Invitation email sent successfully!');
      setAnchorEl(null);
    } catch (error) {
      console.error('Error resending invitation:', error);
      showSnackbar(error.response?.data?.message || 'Error sending invitation', 'error');
    }
  };

  const handleResetPassword = async (userId) => {
    if (window.confirm('Are you sure you want to send a password reset email to this user?')) {
      try {
        await api.post(`/admin/client-users/${userId}/reset-password`, {
          companyId
        });
        showSnackbar('Password reset email sent successfully!');
        setAnchorEl(null);
      } catch (error) {
        console.error('Error sending password reset:', error);
        showSnackbar(error.response?.data?.message || 'Error sending password reset', 'error');
      }
    }
  };

  const handleRemoveUser = async (userId) => {
    if (window.confirm('Are you sure you want to remove this client user from your company?')) {
      try {
        await api.delete(`/admin/companies/${companyId}/client-users/${userId}`);
        showSnackbar('Client user removed successfully!');
        fetchData(); // Refresh the list
        setAnchorEl(null);
      } catch (error) {
        console.error('Error removing client user:', error);
        showSnackbar(error.response?.data?.message || 'Error removing client user', 'error');
      }
    }
  };

  const openEditDialog = (clientUser) => {
    setSelectedUser(clientUser);
    
    // Populate custom fields from user or use defaults
    const userCustomFields = {};
    customFields.forEach(field => {
      userCustomFields[field.fieldName] = clientUser.customFields?.[field.fieldName] || field.defaultValue || '';
    });
    
    setFormData({
      email: clientUser.email,
      firstName: clientUser.firstName || '',
      lastName: clientUser.lastName || '',
      additionalInfo: clientUser.additionalInfo || '',
      customFields: userCustomFields,
      viewingPermissions: clientUser.viewingPermissions,
      projectAccess: clientUser.projects.map(p => p.projectId)
    });
    setEditDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, user) => {
    setSelectedUser(user);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // Render custom field based on type
  const renderCustomField = (field) => {
    const value = formData.customFields[field.fieldName] || '';
    
    switch (field.fieldType) {
      case 'text':
        return (
          <TextField
            key={field.fieldName}
            label={field.displayName}
            fullWidth
            value={value}
            onChange={(e) => setFormData({
              ...formData,
              customFields: { ...formData.customFields, [field.fieldName]: e.target.value }
            })}
            helperText={field.helpText || ''}
            sx={{
              '& input': {
                caretColor: '#1976d2 !important',
                color: '#000',
                '&:focus': {
                  caretColor: '#1976d2 !important'
                }
              }
            }}
          />
        );
      
      case 'number':
        return (
          <TextField
            key={field.fieldName}
            label={field.displayName}
            type="number"
            fullWidth
            value={value}
            onChange={(e) => setFormData({
              ...formData,
              customFields: { ...formData.customFields, [field.fieldName]: e.target.value }
            })}
            helperText={field.helpText || ''}
            sx={{
              '& input': {
                caretColor: '#1976d2 !important',
                color: '#000',
                '&:focus': {
                  caretColor: '#1976d2 !important'
                }
              }
            }}
          />
        );
      
      case 'date':
        return (
          <TextField
            key={field.fieldName}
            label={field.displayName}
            type="date"
            fullWidth
            value={value}
            onChange={(e) => setFormData({
              ...formData,
              customFields: { ...formData.customFields, [field.fieldName]: e.target.value }
            })}
            InputLabelProps={{ shrink: true }}
            helperText={field.helpText || ''}
            sx={{
              '& input': {
                caretColor: '#1976d2 !important',
                color: '#000',
                '&:focus': {
                  caretColor: '#1976d2 !important'
                }
              }
            }}
          />
        );
      
      case 'dropdown':
        return (
          <FormControl key={field.fieldName} fullWidth>
            <InputLabel>{field.displayName}</InputLabel>
            <Select
              value={value}
              onChange={(e) => setFormData({
                ...formData,
                customFields: { ...formData.customFields, [field.fieldName]: e.target.value }
              })}
              label={field.displayName}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {field.dropdownOptions && field.dropdownOptions.map((option, idx) => (
                <MenuItem key={idx} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {field.helpText && <FormHelperText>{field.helpText}</FormHelperText>}
          </FormControl>
        );
      
      case 'checkbox':
        return (
          <FormControlLabel
            key={field.fieldName}
            control={
              <Checkbox
                checked={value === true || value === 'true'}
                onChange={(e) => setFormData({
                  ...formData,
                  customFields: { ...formData.customFields, [field.fieldName]: e.target.checked }
                })}
              />
            }
            label={
              <Box>
                <Typography variant="body1">{field.displayName}</Typography>
                {field.helpText && (
                  <Typography variant="caption" color="textSecondary">
                    {field.helpText}
                  </Typography>
                )}
              </Box>
            }
          />
        );
      
      default:
        return null;
    }
  };

  // Custom fields management handlers
  const handleSaveCustomFields = async () => {
    try {
      setSavingCustomFields(true);
      await api.put(`/companies/${companyId}/custom-fields`, {
        customClientUserFields: customFields
      });
      showSnackbar('Custom fields saved successfully!');
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error saving custom fields:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save custom fields', 'error');
    } finally {
      setSavingCustomFields(false);
    }
  };

  const resetFieldForm = () => {
    setFieldForm({
      fieldName: '',
      displayName: '',
      fieldType: 'text',
      dropdownOptions: [],
      defaultValue: '',
      helpText: '',
      userEditable: true,
      order: customFields.length
    });
    setEditingField(null);
  };

  const openAddFieldDialog = () => {
    resetFieldForm();
    setFieldDialogOpen(true);
  };

  const openEditFieldDialog = (field, index) => {
    setFieldForm({
      ...field,
      dropdownOptions: field.dropdownOptions || []
    });
    setEditingField(index);
    setFieldDialogOpen(true);
  };

  const handleSaveField = () => {
    // Validate
    if (!fieldForm.fieldName || !fieldForm.displayName) {
      showSnackbar('Field name and display name are required', 'error');
      return;
    }

    // Generate fieldName from displayName if empty
    let finalFieldName = fieldForm.fieldName;
    if (!finalFieldName && fieldForm.displayName) {
      finalFieldName = fieldForm.displayName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }

    const newField = { ...fieldForm, fieldName: finalFieldName };

    if (editingField !== null) {
      // Edit existing field
      const updatedFields = [...customFields];
      updatedFields[editingField] = newField;
      setCustomFields(updatedFields);
    } else {
      // Add new field
      setCustomFields([...customFields, newField]);
    }

    setFieldDialogOpen(false);
    resetFieldForm();
  };

  const handleDeleteField = (index) => {
    if (window.confirm('Are you sure you want to delete this custom field?')) {
      const updatedFields = customFields.filter((_, i) => i !== index);
      setCustomFields(updatedFields);
    }
  };

  const moveField = (index, direction) => {
    const newFields = [...customFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    
    // Update order values
    newFields.forEach((field, idx) => {
      field.order = idx;
    });
    
    setCustomFields(newFields);
  };

  // Filter and search logic
  const filteredUsers = clientUsers.filter(user => {
    // Status filter
    let statusMatch = true;
    if (statusFilter === 'active') {
      statusMatch = user.isActivated;
    } else if (statusFilter === 'pending') {
      statusMatch = !user.isActivated;
    }
    
    // Search filter
    let searchMatch = true;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      searchMatch = 
        user.email?.toLowerCase().includes(query) ||
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(query);
    }
    
    return statusMatch && searchMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: 4,
        bgcolor: '#f5f5f5',
        minHeight: '100vh',
        '& .MuiPaper-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiCard-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiTableContainer-root': {
          bgcolor: '#ffffff !important',
        }
      }}
    >
      <BreadcrumbsNav />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Client Users Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage client users for {company?.name}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setCustomFieldsDialogOpen(true)}
          >
            Configure Custom Fields
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setInviteDialogOpen(true);
            }}
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
              }
            }}
          >
            Invite Client User
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PersonIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {clientUsers.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {clientUsers.filter(u => u.isActivated).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Activated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <EmailIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {clientUsers.filter(u => !u.isActivated).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <BusinessIcon color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {projects.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Projects
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Client Users Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              Client Users
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {/* Search and Filter Controls */}
          <Box mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Tabs
                  value={statusFilter}
                  onChange={(e, newValue) => setStatusFilter(newValue)}
                  variant="fullWidth"
                  sx={{ 
                    minHeight: 40,
                    '& .MuiTab-root': { 
                      minHeight: 40,
                      py: 1
                    }
                  }}
                >
                  <Tab 
                    label={`All (${clientUsers.length})`} 
                    value="all"
                  />
                  <Tab 
                    label={`Active (${clientUsers.filter(u => u.isActivated).length})`} 
                    value="active"
                  />
                  <Tab 
                    label={`Pending (${clientUsers.filter(u => !u.isActivated).length})`} 
                    value="pending"
                  />
                </Tabs>
              </Grid>
            </Grid>
          </Box>

          {/* Results Summary */}
          {(searchQuery || statusFilter !== 'all') && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Showing {filteredUsers.length} of {clientUsers.length} users
                {searchQuery && ` matching "${searchQuery}"`}
              </Typography>
            </Box>
          )}

          {clientUsers.length === 0 ? (
            <Box textAlign="center" py={8}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No client users yet
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                Start by inviting your first client user to access your workflows.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setInviteDialogOpen(true);
                }}
              >
                Invite First User
              </Button>
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box textAlign="center" py={8}>
              <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No users found
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                Try adjusting your search or filter criteria.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Access Level</TableCell>
                      <TableCell>Projects</TableCell>
                      <TableCell>Added</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((clientUser) => (
                    <TableRow key={clientUser._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ bgcolor: '#667eea' }}>
                            {clientUser.firstName?.charAt(0) || clientUser.email?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {clientUser.firstName && clientUser.lastName 
                                ? `${clientUser.firstName} ${clientUser.lastName}`
                                : 'Pending Setup'
                              }
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {clientUser.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={clientUser.isActivated ? 'Active' : 'Pending'}
                          color={clientUser.isActivated ? 'success' : 'warning'}
                          size="small"
                          icon={clientUser.isActivated ? <CheckCircleIcon /> : <EmailIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={clientUser.viewingPermissions === 'all_executions' ? 'Manager' : 'Staff'}
                          variant="outlined"
                          size="small"
                          color={clientUser.viewingPermissions === 'all_executions' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {clientUser.projectCount} project{clientUser.projectCount !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(clientUser.addedAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, clientUser)}
                          size="small"
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
                <Typography variant="body2" color="textSecondary">
                  Page {currentPage} of {totalPages} ({filteredUsers.length} total users)
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => openEditDialog(selectedUser)}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Edit User" />
        </MenuItem>
        {selectedUser && !selectedUser.isActivated && (
          <MenuItem onClick={() => handleResendInvitation(selectedUser._id)}>
            <ListItemIcon>
              <SendIcon />
            </ListItemIcon>
            <ListItemText primary="Resend Invitation" />
          </MenuItem>
        )}
        {selectedUser && selectedUser.isActivated && (
          <MenuItem onClick={() => handleResetPassword(selectedUser._id)}>
            <ListItemIcon>
              <LockIcon />
            </ListItemIcon>
            <ListItemText primary="Reset Password" />
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleRemoveUser(selectedUser?._id)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Remove User" />
        </MenuItem>
      </Menu>

      {/* Invite Dialog */}
      <Dialog 
        open={inviteDialogOpen} 
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PersonIcon color="primary" />
            Invite Client User
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              helperText="The user will receive an invitation email at this address"
            />
            
            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                fullWidth
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              />
            </Box>

            <TextField
              label="Additional Information"
              fullWidth
              multiline
              rows={3}
              value={formData.additionalInfo}
              onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
              helperText="Optional: Add any additional information about this user"
            />

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Custom Fields" size="small" />
                </Divider>
                {customFields.map(field => renderCustomField(field))}
              </>
            )}

            <FormControl fullWidth>
              <InputLabel>Access Level</InputLabel>
              <Select
                value={formData.viewingPermissions}
                onChange={(e) => setFormData({...formData, viewingPermissions: e.target.value})}
                label="Access Level"
              >
                <MenuItem value="own_executions">Staff (View Own Executions Only)</MenuItem>
                <MenuItem value="all_executions">Manager (View All Executions)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Project Access</InputLabel>
              <Select
                multiple
                value={formData.projectAccess}
                onChange={(e) => setFormData({...formData, projectAccess: e.target.value})}
                label="Project Access"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const project = projects.find(p => p._id === value);
                      return (
                        <Chip key={value} label={project?.name || value} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {projects.length === 0 && (
                  <MenuItem disabled>
                    No projects available
                  </MenuItem>
                )}
                {projects.map((project) => {
                  console.log('🎯 [RENDER] Rendering project:', project.name, project._id);
                  return (
                    <MenuItem key={project._id} value={project._id}>
                      {project.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              The user will receive an email invitation with instructions to activate their account.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleInviteSubmit}
            variant="contained"
            disabled={creating || !formData.email}
            startIcon={creating ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {creating ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <EditIcon color="primary" />
            Edit Client User
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Email Address"
              fullWidth
              disabled
              value={formData.email}
            />
            
            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                fullWidth
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              />
            </Box>

            <TextField
              label="Additional Information"
              fullWidth
              multiline
              rows={3}
              value={formData.additionalInfo}
              onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
              helperText="Optional: Add any additional information about this user"
            />

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Custom Fields" size="small" />
                </Divider>
                {customFields.map(field => renderCustomField(field))}
              </>
            )}

            <FormControl fullWidth>
              <InputLabel>Access Level</InputLabel>
              <Select
                value={formData.viewingPermissions}
                onChange={(e) => setFormData({...formData, viewingPermissions: e.target.value})}
                label="Access Level"
              >
                <MenuItem value="own_executions">Staff (View Own Executions Only)</MenuItem>
                <MenuItem value="all_executions">Manager (View All Executions)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Project Access</InputLabel>
              <Select
                multiple
                value={formData.projectAccess}
                onChange={(e) => setFormData({...formData, projectAccess: e.target.value})}
                label="Project Access"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const project = projects.find(p => p._id === value);
                      return (
                        <Chip key={value} label={project?.name || value} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {projects.map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {creating ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Fields Configuration Dialog */}
      <Dialog
        open={customFieldsDialogOpen}
        onClose={() => setCustomFieldsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <BadgeIcon color="primary" />
              Configure Custom Fields
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddFieldDialog}
              size="small"
            >
              Add Field
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Custom fields will be available when inviting or editing client users. 
              Values can be used in flows as variables like <code>{'{{clientUser.customField.fieldName}}'}</code>
            </Alert>

            {customFields.length === 0 ? (
              <Box textAlign="center" py={6}>
                <BadgeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Custom Fields Yet
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  Add custom fields to collect additional information from your client users.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openAddFieldDialog}
                >
                  Add First Field
                </Button>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Field Name (Variable)</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>User Editable</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customFields.map((field, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {field.displayName}
                          </Typography>
                          {field.helpText && (
                            <Typography variant="caption" color="textSecondary">
                              {field.helpText}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Variable resolves based on the project's company context">
                            <Chip 
                              label={`{{clientUser.customField.${field.fieldName}}}`} 
                              size="small" 
                              variant="outlined"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip label={field.fieldType} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={field.userEditable !== false ? 'Yes' : 'No'} 
                            size="small" 
                            color={field.userEditable !== false ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Move Up">
                              <IconButton
                                size="small"
                                onClick={() => moveField(index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Move Down">
                              <IconButton
                                size="small"
                                onClick={() => moveField(index, 'down')}
                                disabled={index === customFields.length - 1}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => openEditFieldDialog(field, index)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteField(index)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCustomFieldsDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCustomFields}
            disabled={savingCustomFields}
            startIcon={savingCustomFields ? <CircularProgress size={20} /> : null}
          >
            {savingCustomFields ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Field Dialog */}
      <Dialog
        open={fieldDialogOpen}
        onClose={() => setFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingField !== null ? 'Edit Custom Field' : 'Add Custom Field'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Display Name"
              fullWidth
              required
              value={fieldForm.displayName}
              onChange={(e) => setFieldForm({...fieldForm, displayName: e.target.value})}
              helperText="The label shown to users"
            />

            <TextField
              label="Field Name (Variable)"
              fullWidth
              required
              value={fieldForm.fieldName}
              onChange={(e) => setFieldForm({...fieldForm, fieldName: e.target.value})}
              helperText="Use lowercase letters, numbers, and underscores only"
              placeholder="e.g., employee_id"
            />

            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={fieldForm.fieldType}
                onChange={(e) => setFieldForm({...fieldForm, fieldType: e.target.value})}
                label="Field Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="dropdown">Dropdown</MenuItem>
                <MenuItem value="checkbox">Checkbox</MenuItem>
              </Select>
            </FormControl>

            {fieldForm.fieldType === 'dropdown' && (
              <TextField
                label="Dropdown Options"
                fullWidth
                multiline
                rows={4}
                value={fieldForm.dropdownOptions.join('\n')}
                onChange={(e) => setFieldForm({
                  ...fieldForm,
                  dropdownOptions: e.target.value.split('\n').filter(o => o.trim())
                })}
                helperText="Enter each option on a new line"
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            )}

            <TextField
              label="Default Value"
              fullWidth
              value={fieldForm.defaultValue}
              onChange={(e) => setFieldForm({...fieldForm, defaultValue: e.target.value})}
              helperText="Optional default value for this field"
            />

            <TextField
              label="Help Text"
              fullWidth
              multiline
              rows={2}
              value={fieldForm.helpText}
              onChange={(e) => setFieldForm({...fieldForm, helpText: e.target.value})}
              helperText="Optional help text shown to users"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldForm.userEditable !== false}
                  onChange={(e) => setFieldForm({...fieldForm, userEditable: e.target.checked})}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">User Editable</Typography>
                  <Typography variant="caption" color="textSecondary">
                    If enabled, users can edit this field value in their profile page
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveField}
          >
            {editingField !== null ? 'Update Field' : 'Add Field'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientUsersPage; 